import React, { useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Bold, Italic, Link2, Image as ImageIcon, List, ListOrdered, Heading2, Pilcrow } from 'lucide-react';

const RichTextEditor = ({ value, onChange }) => {
  const ref = useRef(null);

  // Sync external value into the editor without disrupting the caret while typing
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      const next = DOMPurify.sanitize(value || '', { ADD_ATTR: ['target'] });
      if (ref.current.innerHTML !== next) {
        ref.current.innerHTML = next;
      }
    }
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    // Embedded base64/data-URI images are not allowed: they bloat the article and
    // break saving (oversized request). Users add images via the "Insert image (URL)" button.
    const dataImgs = ref.current.querySelectorAll('img[src^="data:"]');
    if (dataImgs.length) dataImgs.forEach((img) => img.remove());
    onChange(ref.current.innerHTML);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const cb = e.clipboardData || window.clipboardData;
    const text = cb ? cb.getData('text/plain') : '';
    if (text) document.execCommand('insertText', false, text);
    emit();
  };

  const handleDrop = (e) => {
    // Block dropping image files (which would embed as base64)
    e.preventDefault();
  };

  const apply = (command, arg = null) => {
    if (ref.current) ref.current.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const insertImage = () => {
    const url = window.prompt('Paste the image URL (https://...)');
    if (url) apply('insertImage', url.trim());
  };

  const insertLink = () => {
    const url = window.prompt('Paste the link URL (https://...)');
    if (url) apply('createLink', url.trim());
  };

  const btnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#111111',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  };

  const tools = [
    { id: 'bold', icon: Bold, title: 'Bold', action: () => apply('bold') },
    { id: 'italic', icon: Italic, title: 'Italic', action: () => apply('italic') },
    { id: 'h2', icon: Heading2, title: 'Heading', action: () => apply('formatBlock', '<h2>') },
    { id: 'paragraph', icon: Pilcrow, title: 'Paragraph', action: () => apply('formatBlock', '<p>') },
    { id: 'ul', icon: List, title: 'Bullet list', action: () => apply('insertUnorderedList') },
    { id: 'ol', icon: ListOrdered, title: 'Numbered list', action: () => apply('insertOrderedList') },
    { id: 'link', icon: Link2, title: 'Insert link', action: insertLink },
    { id: 'image', icon: ImageIcon, title: 'Insert image (URL)', action: insertImage }
  ];

  return (
    <div data-testid="rich-text-editor">
      <div style={{ border: '1px solid #E0E0E0', borderRadius: '4px', overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          gap: '2px',
          padding: '6px 8px',
          borderBottom: '1px solid #E0E0E0',
          backgroundColor: '#F4F1E8',
          flexWrap: 'wrap'
        }}
      >
        {tools.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              title={t.title}
              aria-label={t.title}
              data-testid={`rte-${t.id}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={t.action}
              style={btnStyle}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E8E2D2'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Icon size={16} />
            </button>
          );
        })}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        data-testid="rte-content"
        data-placeholder="Write the article here. Use the toolbar to format text and insert images."
        className="rte-editor"
        style={{
          minHeight: '240px',
          maxHeight: '500px',
          overflowY: 'auto',
          padding: '14px',
          fontSize: '0.95rem',
          lineHeight: '1.7',
          outline: 'none',
          fontFamily: 'var(--font-body)',
          color: '#111111'
        }}
      />
      </div>
      <p style={{ fontSize: '0.8rem', color: '#6B6760', margin: '8px 2px 0' }}>
        Tip: add images using the image button (paste an image URL). Text is pasted as plain text; image files can't be embedded directly.
      </p>
    </div>
  );
};

export default RichTextEditor;
